import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface CanvasReaction {
  id: string;
  canvas_item_id: string;
  user_id: string | null;
  reaction_type: 'like' | 'sticker';
  sticker_emoji?: string;
  sticker_data?: any;
  created_at: string;
}

export function useCanvasReactions(itemId: string) {
  const { user } = useAuth();
  const [reactions, setReactions] = useState<CanvasReaction[]>([]);
  const [likeCount, setLikeCount] = useState(0);
  const [hasLiked, setHasLiked] = useState(false);
  const [loading, setLoading] = useState(false);

  // Load reactions for this item
  const loadReactions = useCallback(async () => {
    if (!itemId) return;

    try {
      const { data, error } = await supabase
        .from('canvas_item_reactions')
        .select('*')
        .eq('canvas_item_id', itemId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setReactions(data || []);
      
      // Calculate like count and check if current user has liked
      const likes = (data || []).filter(r => r.reaction_type === 'like');
      setLikeCount(likes.length);
      setHasLiked(likes.some(r => r.user_id === user?.id));
    } catch (error) {
      console.error('Error loading reactions:', error);
    }
  }, [itemId, user?.id]);

  // Toggle like
  const toggleLike = useCallback(async () => {
    if (!user || !itemId) return;

    setLoading(true);
    try {
      if (hasLiked) {
        // Unlike
        const { error } = await supabase
          .from('canvas_item_reactions')
          .delete()
          .eq('canvas_item_id', itemId)
          .eq('user_id', user.id)
          .eq('reaction_type', 'like');

        if (error) throw error;
        
        setHasLiked(false);
        setLikeCount(prev => Math.max(0, prev - 1));
      } else {
        // Like
        const { error } = await supabase
          .from('canvas_item_reactions')
          .insert({
            canvas_item_id: itemId,
            user_id: user.id,
            reaction_type: 'like'
          });

        if (error) throw error;
        
        setHasLiked(true);
        setLikeCount(prev => prev + 1);
      }

      // Reload to get fresh data
      loadReactions();
    } catch (error) {
      console.error('Error toggling like:', error);
    } finally {
      setLoading(false);
    }
  }, [user, itemId, hasLiked, loadReactions]);

  // Add sticker reaction
  const addSticker = useCallback(async (emoji: string, stickerData: any) => {
    if (!user || !itemId) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('canvas_item_reactions')
        .insert({
          canvas_item_id: itemId,
          user_id: user.id,
          reaction_type: 'sticker',
          sticker_emoji: emoji,
          sticker_data: stickerData
        });

      if (error) throw error;
      
      // Reload reactions
      loadReactions();
    } catch (error) {
      console.error('Error adding sticker:', error);
    } finally {
      setLoading(false);
    }
  }, [user, itemId, loadReactions]);

  // Subscribe to realtime changes
  useEffect(() => {
    loadReactions();

    const channel = supabase
      .channel(`canvas_reactions:${itemId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'canvas_item_reactions',
          filter: `canvas_item_id=eq.${itemId}`
        },
        () => {
          loadReactions();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [itemId, loadReactions]);

  return {
    reactions,
    likeCount,
    hasLiked,
    loading,
    toggleLike,
    addSticker,
    stickers: reactions.filter(r => r.reaction_type === 'sticker')
  };
}
